/**
 * Generic Element Tracking
 */
export default class GenericTracking {
	constructor() {
		this.isAmazonLP = window.location.href.includes('/benefits/amazon');
		this.EVENTS = {
			BENEFIT_CARD_CLICK: 'Benefit Card Click',
			BENEFIT_CARD_CTA_CLICK: 'Benefit Card CTA Click',
			FAQ_CLICK: 'FAQ Click',
			FIND_THERAPIST_CTA_CLICK: 'Find a Therapist CTA Click',
			FIND_THERAPIST_CTA_NAV_CLICK: 'Find a Therapist CTA Nav Click',
			HOW_IT_WORKS_LINK_CLICK: 'How it works Link Click',
			INSURANCE_LOGO_CLICK: 'Insurance Logo Click',
			INTERCOM_CLICK: 'Get in Touch',
			REVIEWS_CAROUSEL: 'Reviews Carousel',
			SCREENER_CARDS_CLICK: 'Screener Cards',
			WPML_LANGUAGE_OPTION_CLICK: 'Language Switcher Modal Choice',
		};
		this.FIND_THERAPIST_TEXT = 'find a therapist';
		this.EVENT_PATTERNS = {
			MOBILE: 'Mobile',
			TOGGLE_TYPES: [
				'By City',
				'By State',
				'Provider Resources',
				'Therapy Resources',
			],
		};
		this.MADLIBS_FILTERS_URL = '/start/filters/';
		this.SELECTORS = {
			CTA_SELECTORS:
				'[data-header-interstitial-trigger], [data-sticky-mobile-cta-trigger]',
			INTERCOM_TRIGGER: '[data-intercom-trigger]',
			MOBILE_NAV: '.header__nav--mobile [data-segment-click-mobile-only]',
			HEADER_DROPDOWN_SUBMENU: '.header__dropdown--sub-menu',
			HEADER_INTERSTITIAL_TRIGGER: '[data-header-interstitial-trigger]',
			HEADER_SEGMENT_CLICK: 'header li[data-segment-click]',
			STICKY_MOBILE_CTA_TRIGGER: '[data-sticky-mobile-cta-trigger]',
			SUBMENU_TOGGLE: '.brx-submenu-toggle span',
		};
		this.TRACKED_ATTR = 'data-tracked';

		this.cache = new Map();

		this.configs = [
			...this.createAccordionConfigs(),
			...this.createBasicConfigs(),
			...this.createHeaderNavConfigs(),
			...this.createMobileNavToggleConfigs(),
			...this.createScreenerConfigs(),
			...this.createStatesListConfigs(),
			...this.createToggleConfigs(),
			...this.createWpmlModalConfigs(),
		];

		this.initialized = false;
		this.init();
	}

	init() {
		if (this.initialized) return;
		this.setupTracking();
		this.initialized = true;
	}

	attachTracking(element, configs) {
		if (element.hasAttribute(this.TRACKED_ATTR)) return;

		element.setAttribute(this.TRACKED_ATTR, 'true');
		const configArray = Array.isArray(configs) ? configs : [configs];

		element.addEventListener(
			'click',
			(event) => {
				configArray.forEach((config) => {
					if (this.shouldSkipTracking(event, config)) return;

					this.trackEvent(config, element);
				});
			},
			{ passive: true },
		);
	}

	/**
	 * Cache utility for expensive operations
	 * @param {string}   key       - Cache key
	 * @param {Function} computeFn - Function to compute value if not cached
	 * @return {any} Cached or computed value
	 */
	cacheGet(key, computeFn) {
		if (!this.cache.has(key)) {
			this.cache.set(key, computeFn());
		}
		return this.cache.get(key);
	}

	/**
	 * Create accordion tracking configurations
	 * @return {Array} Array of accordion configs
	 */
	createAccordionConfigs() {
		return [
			this.createPositionConfig(
				'.accordion-wrapper__accordion-nested .accordion-wrapper__item',
				this.EVENTS.FAQ_CLICK,
				'faq_number',
				'.accordion-wrapper__accordion-nested',
				'.accordion-wrapper__item',
			),
		];
	}

	/**
	 * Create basic tracking configurations
	 * @return {Array} Array of basic configs
	 */
	createBasicConfigs() {
		return [
			this.createComponentConfig(
				'a[href*="/cost-estimate"]',
				'Cost Estimate Link Click',
				(element) => ({ link_text: element.textContent?.trim() || '' }),
			),
			{
				selector: 'a[href^="/client-login"]',
				eventType: 'Client Login',
			},
			{
				selector: 'a[href="https://provider.growtherapy.com/login"]',
				eventType: 'Provider Login',
			},
			this.createComponentConfig(
				'[data-specialty]',
				'Specialty Name Click',
				(element) => ({
					specialty_name: element.getAttribute('data-specialty') || '',
				}),
			),
			this.createComponentConfig(
				'[data-insurance]',
				this.EVENTS.INSURANCE_LOGO_CLICK,
				(element) => ({
					insurance_name: element.getAttribute('data-insurance') || '',
				}),
			),
			{
				selector: '.dark-content-image-layout .dark-content-card a',
				eventType: this.EVENTS.HOW_IT_WORKS_LINK_CLICK,
				getProperties: (element) => ({
					step_number: this.getHowItWorksStepNumber(element),
				}),
			},
			{
				selector:
					'[data-segment-component-name="Testimonial Carousel"] .splide__arrow',
				eventType: this.EVENTS.REVIEWS_CAROUSEL,
			},
			{
				selector: this.SELECTORS.INTERCOM_TRIGGER,
				eventType: this.EVENTS.INTERCOM_CLICK,
			},
		];
	}

	/**
	 * Create a configuration with component name and custom properties
	 *
	 * @param {string}   selector             - CSS selector
	 * @param {string}   eventType            - Event type name
	 * @param {Function} additionalProperties - Function to get additional properties
	 * @return {Object} Configuration object
	 */
	createComponentConfig(selector, eventType, additionalProperties = null) {
		const eventTypeName =
			this.isAmazonLP && eventType === this.EVENTS.FIND_THERAPIST_CTA_CLICK
				? 'Find a Therapist Clicked'
				: eventType;

		return {
			selector,
			eventType: eventTypeName,
			getProperties: (element) => ({
				component_name: this.getComponentNameFromElement(element),
				...(additionalProperties ? additionalProperties(element) : {}),
			}),
		};
	}

	/**
	 * Create header navigation configurations
	 * @return {Array} Array of header nav configs
	 */
	createHeaderNavConfigs() {
		const headerNavItems = [
			{ path: '/how-it-works/', eventType: 'Nav - How It Works Click' },
			{
				path: '/providers-medication-management/',
				eventType: 'Nav - Prescribers Click',
			},
			{
				path: '/providers/',
				eventType: 'Nav - Therapists/Counselors Click',
			},
			{ path: '/employers/', eventType: 'Nav - Employers Click' },
			{ path: '/pcp-physicians/', eventType: 'Nav - Physicians Click' },
			{ path: '/payors/', eventType: 'Nav - Payors Click' },
			{ path: '/about-us/', eventType: 'Nav - About Us Click' },
		];

		const findTherapistFilter = this.createTextFilter(this.FIND_THERAPIST_TEXT);

		return [
			...headerNavItems.map((item) => ({
				selector: `header a[href*="${item.path}"]`,
				eventType: item.eventType,
			})),
			{
				selector: `.header__nav--mobile ${this.SELECTORS.STICKY_MOBILE_CTA_TRIGGER}`,
				eventType: this.EVENTS.FIND_THERAPIST_CTA_NAV_CLICK,
				filter: findTherapistFilter,
			},
			{
				selector: `header a[href*="${this.MADLIBS_FILTERS_URL}"]`,
				eventType: this.EVENTS.FIND_THERAPIST_CTA_NAV_CLICK,
				filter: findTherapistFilter,
			},
			this.createComponentConfig(
				`main ${this.SELECTORS.HEADER_INTERSTITIAL_TRIGGER}`,
				this.EVENTS.FIND_THERAPIST_CTA_CLICK,
				null,
			),
			this.createComponentConfig(
				`main ${this.SELECTORS.STICKY_MOBILE_CTA_TRIGGER}`,
				this.EVENTS.FIND_THERAPIST_CTA_CLICK,
				null,
			),
		].map((config) => {
			if (config.eventType === this.EVENTS.FIND_THERAPIST_CTA_CLICK) {
				config.filter = findTherapistFilter;
			}

			return config;
		});
	}

	/**
	 * Create mobile nav toggle configurations
	 * @return {Array} Array of toggle configurations
	 */
	createMobileNavToggleConfigs() {
		const mobileNavToggles = [
			{ text: 'get care', eventType: 'Nav - Get Care Click - Mobile' },
			{
				text: 'become a grow provider',
				eventType: 'Nav - Become Provider Click - Mobile',
			},
			{
				text: 'partner with grow',
				eventType: 'Nav - Partner with Grow Click - Mobile',
			},
		];

		return mobileNavToggles.map((toggle) => ({
			selector: this.SELECTORS.MOBILE_NAV,
			eventType: toggle.eventType,
			filter: this.createTextFilter(
				toggle.text,
				this.SELECTORS.SUBMENU_TOGGLE,
				'exact',
			),
		}));
	}

	/**
	 * Create a configuration with position-based properties
	 * @param {string} selector          - CSS selector
	 * @param {string} eventType         - Event type name
	 * @param {string} positionProperty  - Property name for position
	 * @param {string} containerSelector - Container selector for position calculation
	 * @param {string} itemSelector      - Item selector for position calculation
	 * @return {Object} Configuration object
	 */
	createPositionConfig(
		selector,
		eventType,
		positionProperty,
		containerSelector,
		itemSelector,
	) {
		return {
			selector,
			eventType,
			getProperties: (element) => ({
				[positionProperty]: this.getElementPosition(
					element,
					containerSelector,
					itemSelector,
				),
			}),
		};
	}

	/**
	 * Create screener cards tracking configurations
	 * @return {Array} Array of screener configs
	 */
	createScreenerConfigs() {
		return [
			this.createPositionConfig(
				'.screeners-grid__posts-wrapper .related-content',
				this.EVENTS.SCREENER_CARDS_CLICK,
				'card_number',
				'.screeners-grid__posts-wrapper',
				'.related-content',
			),
		];
	}

	/**
	 * Create states list tracking configurations
	 * @return {Array} Array of states list configs
	 */
	createStatesListConfigs() {
		return [
			this.createTextConfig(
				'header [data-states-list-link]',
				'Nav - State Click',
			),
			this.createTextConfig(
				'header [data-states-by-city-link]',
				'Nav - City Click',
			),
		];
	}

	/**
	 * Create a configuration with text-based properties
	 * @param {string} selector     - CSS selector
	 * @param {string} eventType    - Event type name
	 * @param {string} propertyName - Property name for text content
	 * @return {Object} Configuration object
	 */
	createTextConfig(selector, eventType, propertyName = 'value') {
		return {
			selector,
			eventType,
			getProperties: (element) => ({
				[propertyName]: element.textContent?.trim() || '',
			}),
		};
	}

	/**
	 * Create reusable text filter function
	 * @param {string} text      - Text to match
	 * @param {string} selector  - Optional selector to find specific element
	 * @param {string} matchType - 'includes' or 'exact' match
	 * @return {Function} Filter function
	 */
	createTextFilter(text, selector = null, matchType = 'includes') {
		const lowerText = text.toLowerCase();

		return (element) => {
			const targetElement = selector
				? element.querySelector(selector)
				: element;
			const elementText = targetElement?.textContent?.trim().toLowerCase();

			if (!elementText) return false;

			return matchType === 'exact'
				? elementText === lowerText
				: elementText.includes(lowerText);
		};
	}

	/**
	 * Create toggle configurations for header segment clicks
	 * @return {Array} Array of toggle configs
	 */
	createToggleConfigs() {
		const toggleItems = [
			{ text: 'by state', eventType: 'Nav - By State Click' },
			{ text: 'by city', eventType: 'Nav - By City Click' },
			{
				text: 'therapy resources',
				eventType: 'Nav - Therapy Resources Click',
			},
			{
				text: 'provider resources',
				eventType: 'Nav - Provider Resources Click',
			},
		];

		return toggleItems.map((item) => ({
			selector: this.SELECTORS.HEADER_SEGMENT_CLICK,
			eventType: item.eventType,
			filter: this.createTextFilter(
				item.text,
				this.SELECTORS.SUBMENU_TOGGLE,
				'exact',
			),
		}));
	}

	/**
	 * Create WPML/responsive modal click tracking configurations
	 * @return {Array} Array of modal-related configs
	 */
	createWpmlModalConfigs() {
		return [
			{
				selector: '[data-responsive-modal-trigger]',
				getEventType: (element) =>
					this.getResponsiveModalTriggerEventType(element),
				getProperties: (element) => ({
					component_name: this.getComponentNameFromElement(element),
					modal_id: element.getAttribute('data-responsive-modal-trigger') || '',
					trigger_location: this.getResponsiveModalTriggerLocation(element),
				}),
			},
			{
				selector: '.wpml-language-switcher-form input[type="radio"]',
				getEventType: (element) => this.getWpmlLanguageOptionEventType(element),
				getProperties: (element) => ({
					component_name: this.getComponentNameFromElement(element),
					selected_language: element.value || '',
				}),
			},
			{
				selector: '[data-responsive-modal-close]',
				getEventType: (element) =>
					this.getResponsiveModalCloseEventType(element),
				getProperties: (element) => ({
					component_name: this.getComponentNameFromElement(element),
					modal_id: this.getResponsiveModalIdFromElement(element),
				}),
			},
		];
	}

	/**
	 * Get the card number from a CTA button inside a benefit card
	 * @param {Element} ctaElement - The clicked CTA element
	 * @return {number} The card number (1-based)
	 */
	getCardNumberFromCTA(ctaElement) {
		const card = ctaElement.closest('.keen-slider__slide');
		return card
			? this.getElementPosition(
					card,
					'.care-support-section-slider',
					'.keen-slider__slide',
				)
			: 1;
	}

	/**
	 * Get component name from element's data-segment-component-name attribute
	 * Traverses up the DOM tree to find the closest element with the attribute
	 * @param {Element} element - The element to start searching from
	 * @return {string} The component name or empty string if not found
	 */
	getComponentNameFromElement(element) {
		const componentElement = element.closest('[data-segment-component-name]');
		return componentElement?.getAttribute('data-segment-component-name') || '';
	}

	/**
	 * Infer where a responsive modal trigger was clicked
	 * @param {Element} element - Trigger element
	 * @return {string} Location label
	 */
	getResponsiveModalTriggerLocation(element) {
		if (element.closest('header')) return 'header';
		if (element.closest('footer')) return 'footer';
		return 'other';
	}

	/**
	 * Resolve responsive modal id from a close button
	 * @param {Element} element - Close button element
	 * @return {string} Modal id or empty string
	 */
	getResponsiveModalIdFromElement(element) {
		return (
			element
				.closest('[data-responsive-modal-id]')
				?.getAttribute('data-responsive-modal-id') || ''
		);
	}

	/**
	 * Close control: include modal id in the tracked type string.
	 *
	 * @param {Element} element - Close button element
	 * @return {string} Event type
	 */
	getResponsiveModalCloseEventType(element) {
		const modalId = this.getResponsiveModalIdFromElement(element) || 'unknown';
		return `Responsive Modal Close [${modalId}] - Click`;
	}

	/**
	 * Build modal trigger event type: `data-segment-component-name` + `[Header|Footer|Other] - Click`,
	 * or fallback to `Responsive Modal Open [${modalId}] - Click` when no component name is set.
	 *
	 * @param {Element} element - Trigger element
	 * @return {string} Event type
	 */
	getResponsiveModalTriggerEventType(element) {
		const componentName = this.getComponentNameFromElement(element);
		const locationKey = this.getResponsiveModalTriggerLocation(element);
		const locationLabels = {
			header: 'Header',
			footer: 'Footer',
			other: 'Other',
		};
		const locationLabel = locationLabels[locationKey] || 'Other';

		if (componentName) {
			return `${componentName} [${locationLabel}] - Click`;
		}

		const modalId =
			element.getAttribute('data-responsive-modal-trigger') || 'unknown';
		return `Responsive Modal Open [${modalId}] - Click`;
	}

	/**
	 * Language switcher radio: include chosen language code in the tracked type string.
	 *
	 * @param {Element} element - Radio input element
	 * @return {string} Event type
	 */
	getWpmlLanguageOptionEventType(element) {
		const lang = (element.value || '').trim() || 'unknown';
		return `${this.EVENTS.WPML_LANGUAGE_OPTION_CLICK} [${lang}] - Click`;
	}

	/**
	 * Generic method to get element position within a container
	 * @param {Element} element           - The element to find position for
	 * @param {string}  containerSelector - CSS selector for the container
	 * @param {string}  itemSelector      - CSS selector for items within container
	 * @return {number} The position number (1-based)
	 */
	getElementPosition(element, containerSelector, itemSelector) {
		const container = element.closest(containerSelector);
		if (!container) return 1;

		const allItems = container.querySelectorAll(itemSelector);
		const itemIndex = Array.from(allItems).indexOf(element);

		return Math.max(1, itemIndex + 1);
	}

	/**
	 * Get the step number for "How it works" cards based on card position
	 * @param {Element} linkElement - The clicked link element
	 * @return {number} The step number (1-based)
	 */
	getHowItWorksStepNumber(linkElement) {
		const card = linkElement.closest('.dark-content-card');
		if (!card) return 1;

		const container = card.closest('.dark-content-image-layout');
		if (!container) return 1;

		const allCards = container.querySelectorAll('.dark-content-card');
		const cardIndex = Array.from(allCards).indexOf(card);

		return Math.max(1, cardIndex + 1);
	}

	/**
	 * Group configurations by selector to avoid duplicate DOM queries
	 * @return {Object} Object with selectors as keys and config arrays as values
	 */
	groupConfigsBySelector() {
		const grouped = {};

		for (const config of this.configs) {
			if (!grouped[config.selector]) {
				grouped[config.selector] = [];
			}
			grouped[config.selector].push(config);
		}

		return grouped;
	}

	/**
	 * Generic method to check if element has attribute or is inside element with attribute
	 * @param {Element} element   - The element to check
	 * @param {string}  attribute - The attribute to check for
	 * @return {boolean} True if element has the attribute or is a child of one that does
	 */
	hasAttributeOrInParent(element, attribute) {
		return (
			element.hasAttribute(attribute) ||
			element.closest(`[${attribute}]`) !== null
		);
	}

	/**
	 * Check if element or its parents have data-mobile-sub-menu attribute
	 * @param {Element} element - The element to check
	 * @return {boolean} True if element has the attribute or is a child of one that does
	 */
	hasMobileSubMenuAttribute(element) {
		return this.hasAttributeOrInParent(element, 'data-mobile-sub-menu');
	}

	/**
	 * Check if the config is for a benefits card event
	 * @param {Object} config - The tracking configuration
	 * @return {boolean} True if it's a benefits card event
	 */
	isBenefitCardEvent(config) {
		return config.eventType === this.EVENTS.BENEFIT_CARD_CLICK;
	}

	/**
	 * Check if the click target is on a benefit card CTA (including filter links)
	 * @param {Element} target - The click target element
	 * @return {boolean} True if the click is on a benefit card CTA
	 */
	isClickOnBenefitCardCTA(target) {
		// Check for standard CTA selectors
		if (this.isClickOnCTA(target)) return true;

		// Check for filter links in benefit cards
		return this.matchesOrInParent(
			target,
			`a[href*="${this.MADLIBS_FILTERS_URL}"]`,
		);
	}

	/**
	 * Check if the click target is on a CTA button
	 * @param {Element} target - The click target element
	 * @return {boolean} True if the click is on a CTA button
	 */
	isClickOnCTA(target) {
		return target.closest(this.SELECTORS.CTA_SELECTORS) !== null;
	}

	/**
	 * Check if element is inside a header dropdown submenu
	 * @param {Element} element - The element to check
	 * @return {boolean} True if element is in a submenu
	 */
	isInHeaderSubmenu(element) {
		return element.closest(this.SELECTORS.HEADER_DROPDOWN_SUBMENU) !== null;
	}

	/**
	 * Check if the config is for a mobile navigation event
	 * @param {Object} config - The tracking configuration
	 * @return {boolean} True if it's a mobile nav event
	 */
	isMobileNavEvent(config) {
		return config.eventType?.includes(this.EVENT_PATTERNS.MOBILE);
	}

	/**
	 * Check if the config is for a toggle event
	 * @param {Object} config - The tracking configuration
	 * @return {boolean} True if it's a toggle event
	 */
	isToggleEvent(config) {
		return this.EVENT_PATTERNS.TOGGLE_TYPES.some((type) =>
			config.eventType?.includes(type),
		);
	}

	/**
	 * Check if SegmentTracking is available
	 * @return {boolean} True if SegmentTracking is available
	 */
	isSegmentTrackingAvailable() {
		return (
			window.SegmentTracking &&
			typeof window.SegmentTracking.trackButtonClick === 'function'
		);
	}

	/**
	 * Generic method to check if element matches selector or is inside element that matches
	 * @param {Element} element  - The element to check
	 * @param {string}  selector - The selector to check against
	 * @return {boolean} True if element matches or is inside matching element
	 */
	matchesOrInParent(element, selector) {
		return element.matches(selector) || element.closest(selector) !== null;
	}

	setupTracking() {
		const configsBySelector = this.groupConfigsBySelector();
		const elementConfigMap = new Map();

		for (const [selector, configs] of Object.entries(configsBySelector)) {
			const elements = this.cacheGet(`elements_${selector}`, () =>
				Array.from(document.querySelectorAll(selector)),
			);

			for (const element of elements) {
				// Find matching configs for this element
				const matchingConfigs = configs.filter(
					(config) => !config.filter || config.filter(element),
				);

				if (matchingConfigs.length > 0) {
					if (!elementConfigMap.has(element)) {
						elementConfigMap.set(element, []);
					}
					elementConfigMap.get(element).push(...matchingConfigs);
				}
			}
		}

		for (const [element, configs] of elementConfigMap) {
			this.attachTracking(element, configs);
		}
	}

	/**
	 * Determine if tracking should be skipped based on event and config
	 * @param {Event}  event  - The click event
	 * @param {Object} config - The tracking configuration
	 * @return {boolean} True if tracking should be skipped
	 */
	shouldSkipTracking(event, config) {
		const target = event.target;

		if (config.eventType === this.EVENTS.BENEFIT_CARD_CLICK) {
			// Skip benefit card slide click if clicking on a benefit card CTA
			return this.isClickOnBenefitCardCTA(target);
		}

		if (config.eventType === this.EVENTS.BENEFIT_CARD_CTA_CLICK) {
			// Skip benefit card CTA click if NOT clicking on a benefit card CTA
			return !this.isClickOnBenefitCardCTA(target);
		}

		if (this.isMobileNavEvent(config)) {
			return this.hasMobileSubMenuAttribute(target);
		}

		if (this.isToggleEvent(config)) {
			return this.isInHeaderSubmenu(target);
		}

		return false;
	}

	/**
	 * Track the event with error handling
	 * @param {Object}  config  - The tracking configuration
	 * @param {Element} element - The element being tracked
	 */
	trackEvent(config, element) {
		if (!this.isSegmentTrackingAvailable()) {
			return;
		}

		let eventType;
		try {
			eventType = config.getEventType
				? config.getEventType(element)
				: config.eventType;
			if (!eventType) return;

			const additionalProperties = config.getProperties?.(element) || {};
			window.SegmentTracking.trackButtonClick(eventType, additionalProperties);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`Tracking error for ${eventType ?? 'unknown'}:`, error);
		}
	}
}
